// =============================================================================
// tipSource — the bin-sourced half of a tipping_log row, flattened.
//
// A tip has exactly one source (delivery | bin | batch — tipping_log_source_xor,
// mig 809). For a BIN source the interesting facts live one embed away: the
// stored bin's number and, since mig 20261019090000, the RECEIVED pallet it
// was stripped from plus the cartons it carried. Both run-detail surfaces
// (Runs.tsx inline detail, RunDetail.tsx print view) flatten the same embed
// the same way; this is the one place that shape is spelled out.
//
// pickEmbed: PostgREST returns a to-one embed as an object, but a wrongly
// inferred cardinality (or a view) can hand back a one-element array. Treat
// both as "the one row or nothing".
// =============================================================================

export interface BinTipSourceFacts {
  bin_number:          string | null
  source_pallet_id:    string | null
  source_carton_count: number | null
}

/** The single embedded row behind a PostgREST to-one embed, or null. */
export function pickEmbed<T>(x: T | T[] | null | undefined): T | null {
  if (Array.isArray(x)) return x[0] ?? null
  return x ?? null
}

export interface BatchTipSourceFacts {
  batch_id:                   string | null
  batch_number:               string | null
  batch_delivery_id:          string | null
  batch_grower:               string | null
  batch_client_ref_no:        string | null
  batch_delivery_note_number: string | null
}

/**
 * The batch facts off a tipping_log row selected with
 * `cooling_batches(batch_number, delivery_id, delivery:deliveries(grower, client_ref_no, delivery_note_number))`.
 * Berry batch tips carry batch_id with delivery_id NULL — without these a
 * berry run showed source-less tip rows (audit FC-15).
 */
export function batchSourceOf(row: { batch_id?: string | null; cooling_batches?: unknown }): BatchTipSourceFacts {
  type Cb = { batch_number?: string | null; delivery_id?: string | null;
              delivery?: { grower?: string | null; client_ref_no?: string | null; delivery_note_number?: string | null } | null }
  const cb  = pickEmbed(row.cooling_batches as Cb | Cb[] | null | undefined)
  const cbd = pickEmbed(cb?.delivery)
  return {
    batch_id:                   row.batch_id ?? null,
    batch_number:               cb?.batch_number ?? null,
    batch_delivery_id:          cb?.delivery_id ?? null,
    batch_grower:               cbd?.grower ?? null,
    batch_client_ref_no:        cbd?.client_ref_no ?? null,
    batch_delivery_note_number: cbd?.delivery_note_number ?? null,
  }
}

/** The bin facts off a tipping_log row selected with `bins(bin_number, source_pallet_id, source_carton_count)`. */
export function binSourceOf(row: { bins?: unknown }): BinTipSourceFacts {
  const bin = pickEmbed(row.bins as Partial<BinTipSourceFacts> | Partial<BinTipSourceFacts>[] | null | undefined)
  return {
    bin_number:          bin?.bin_number          ?? null,
    source_pallet_id:    bin?.source_pallet_id    ?? null,
    source_carton_count: bin?.source_carton_count ?? null,
  }
}

export interface ReceivedFacts { owner: string; thirdParty: boolean }

/**
 * A pallet's "received" facts off a `runs(run_kind, grower, pallet_receipts(ownership))`
 * embed: null for a pallet we packed ourselves. The receipt embed may arrive as
 * an object or a one-element array (a composite unique key, so PostgREST does
 * not always infer to-one).
 */
export type RunsEmbed = { run_kind: string | null; grower: string | null; commodity?: string | null; pallet_receipts: { ownership: string }[] | { ownership: string } | null }

export function receivedFacts(runsRaw: RunsEmbed | RunsEmbed[] | null | undefined): ReceivedFacts | null {
  const runs = pickEmbed(runsRaw)
  if (!runs || runs.run_kind !== 'EXTERNAL_RECEIPT') return null
  const receipt = pickEmbed(runs.pallet_receipts)
  return { owner: runs.grower ?? 'unknown owner', thirdParty: receipt?.ownership === 'THIRD_PARTY' }
}
